<script setup lang="ts">
/**
 * Bannière de consentement cookies — conformité RGPD CNIL.
 *
 * Affichée tant que l'utilisateur n'a pas fait de choix explicite. Le choix
 * est enregistré par `@donoor/utils/consent` : cookie `.donoor.org` (lisible
 * par dashboard.donoor.org ET par api.donoor.org, qui en a besoin pour la
 * Conversions API Meta) DOUBLÉ du `localStorage` historique.
 *
 * Politique :
 *   - Cookies strictement nécessaires (auth, session) : toujours actifs
 *     (pas de consentement requis selon CNIL).
 *   - Cookies analytics (Google Analytics, Plausible) : opt-in.
 *   - Cookies marketing (Facebook Pixel) : opt-in.
 *   - L'utilisateur peut refuser tout ou personnaliser (« Personnaliser »
 *     ouvre la vue détaillée).
 *   - « Refuser » est volontairement discret depuis le 2026-09-18 : sous le
 *     texte, en 12 px souligné, hors de la rangée de boutons. Il reste un
 *     bouton à UN clic, exactement comme « Tout accepter » — la CNIL demande
 *     que refuser soit aussi facile qu'accepter, pas d'un poids visuel égal.
 *
 * Le composant émet `update:consent` à chaque changement — le code de
 * tracking doit écouter et activer/désactiver dynamiquement.
 */
import { ref, computed, onMounted } from "vue";
import {
  buildConsent,
  persistConsent,
  readConsent,
  type CookieConsent,
} from "@donoor/utils/consent";

const route = useRoute();

// Bannière masquée uniquement sur le flow de paiement (UX focus checkout).
// Inclut désormais /a/[slug] (adhésion) et /b/[slug] (billetterie) dont
// l'étape Récapitulatif contient un Card Element inline — la bannière en
// `fixed bottom` couvrait la zone Stripe et bloquait les tests E2E.
const isHiddenRoute = computed(() => {
  const p = route.path;
  return (
    p.startsWith("/donate/") ||
    p.startsWith("/pay/") ||
    p.startsWith("/campaigns/donate/") ||
    /^\/c\/[^/]+\/payment/.test(p) ||
    /^\/a\/[^/]+(\/payment|\/success)?$/.test(p) ||
    /^\/b\/[^/]+(\/payment|\/success)?$/.test(p) ||
    p === "/donation-success"
  );
});

const isVisible = ref(false);
const showDetails = ref(false);
const analyticsConsent = ref(false);
const marketingConsent = ref(false);

const emit = defineEmits<{
  (e: "update:consent", value: CookieConsent): void;
}>();

function acceptAll() {
  const consent = buildConsent(true, true);
  persistConsent(consent);
  emit("update:consent", consent);
  isVisible.value = false;
}

function rejectAll() {
  const consent = buildConsent(false, false);
  persistConsent(consent);
  emit("update:consent", consent);
  isVisible.value = false;
}

function saveCustom() {
  const consent = buildConsent(analyticsConsent.value, marketingConsent.value);
  persistConsent(consent);
  emit("update:consent", consent);
  isVisible.value = false;
}

onMounted(() => {
  const stored = readConsent();
  if (stored) {
    emit("update:consent", stored);
    return;
  }
  // Pas de consentement enregistré → afficher la bannière
  // Petit délai pour éviter le flash au chargement initial
  setTimeout(() => {
    isVisible.value = true;
  }, 800);
});

const hasMadeChoice = computed(() => readConsent() !== null);

defineExpose({ hasMadeChoice, isVisible });
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-full"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-full"
  >
    <div
      v-if="isVisible && !isHiddenRoute"
      class="fixed bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-auto sm:max-w-md z-100 bg-white dark:bg-gray-900 rounded-3xl shadow-[0_20px_60px_-10px_rgba(0,0,0,0.25)] ring-1 ring-gray-200 dark:ring-gray-800"
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
    >
      <!-- Compacte sur téléphone (audit SEO 2026-10-18, validé par Karim) :
           229 px de haut, elle couvrait « Inscrire mon association » sur
           l'accueil et « Faire un don » sur les cagnottes — Google pénalise
           sur mobile ce qui cache le contenu. 152 px désormais : titre lu par
           les lecteurs d'écran seulement, texte en 12 px, marges réduites.
           Ordinateur inchangé. -->
      <div class="px-4 py-3 sm:px-6 sm:py-6">
        <!-- Vue compacte -->
        <div v-if="!showDetails">
          <div class="flex items-start gap-4">
            <div
              class="hidden sm:flex shrink-0 w-10 h-10 rounded-xl bg-black dark:bg-white/10 items-center justify-center"
              aria-hidden="true"
            >
              <UIcon name="i-lucide-cookie" class="size-5 text-white" />
            </div>
            <div class="flex-1 min-w-0">
              <h2
                id="cookie-consent-title"
                class="sr-only sm:not-sr-only text-base font-semibold text-gray-900 dark:text-white"
              >
                {{ $t("partage.cookies.titre") }}
              </h2>
              <p
                id="cookie-consent-description"
                class="text-xs sm:text-sm sm:mt-1 text-gray-600 dark:text-gray-400 leading-relaxed"
              >
                {{ $t("partage.cookies.intro") }}
                <NuxtLink
                  to="/privacy"
                  class="underline hover:text-gray-900 dark:hover:text-white"
                >
                  {{ $t("partage.cookies.enSavoirPlus") }}
                </NuxtLink>
              </p>
              <!-- « Refuser » sous le texte, en plus petit et souligné (demande
                   Karim 2026-09-18) : il quitte la rangée de boutons pour ne
                   plus concurrencer « Tout accepter ».
                   ⚠️ Il reste un VRAI bouton, atteignable en UN clic comme
                   l'acceptation, et à 12 px lisibles : la CNIL exige que
                   refuser soit aussi FACILE qu'accepter (même nombre de
                   clics). Ne pas le réduire davantage, ni le remplacer par un
                   renvoi vers une page de réglages. -->
              <button
                type="button"
                class="mt-2 text-xs text-gray-500 dark:text-gray-400 underline underline-offset-2 hover:text-gray-900 dark:hover:text-white transition-colors"
                @click="rejectAll"
              >
                {{ $t("partage.cookies.refuser") }}
              </button>
            </div>
          </div>

          <div class="flex flex-wrap gap-2 mt-2 sm:mt-4 sm:justify-end">
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              @click="showDetails = true"
            >
              {{ $t("partage.cookies.personnaliser") }}
            </button>
            <button
              type="button"
              class="px-5 py-2 text-sm font-semibold rounded-xl bg-[#212121] dark:bg-white text-white dark:text-gray-900 hover:bg-[#333] dark:hover:bg-gray-100 transition-colors"
              @click="acceptAll"
            >
              {{ $t("partage.cookies.toutAccepter") }}
            </button>
          </div>
        </div>

        <!-- Vue détaillée (personnaliser) -->
        <div v-else class="space-y-4">
          <div class="flex items-center justify-between">
            <h2 class="text-base font-semibold text-gray-900 dark:text-white">
              {{ $t("partage.cookies.personnaliserPreferences") }}
            </h2>
            <button
              type="button"
              class="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              :aria-label="$t('commun.actions.retour')"
              @click="showDetails = false"
            >
              <UIcon
                name="i-lucide-x"
                class="size-4 text-gray-500 dark:text-gray-400"
              />
            </button>
          </div>

          <div class="space-y-3">
            <!-- Cookies nécessaires (toujours actifs) -->
            <div
              class="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl"
            >
              <div class="flex-1">
                <p
                  class="text-sm font-medium text-gray-900 dark:text-white"
                >
                  {{ $t("partage.cookies.necessaires") }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {{ $t("partage.cookies.necessairesDescription") }}
                </p>
              </div>
              <div
                class="shrink-0 px-2.5 py-1 text-xs font-semibold rounded-md bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
              >
                {{ $t("partage.cookies.toujoursActif") }}
              </div>
            </div>

            <!-- Analytics -->
            <label
              class="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl cursor-pointer"
            >
              <input
                v-model="analyticsConsent"
                type="checkbox"
                class="mt-0.5 size-4 rounded border-gray-300 dark:border-gray-600 text-[#212121] focus:ring-[#212121]"
              />
              <div class="flex-1">
                <p
                  class="text-sm font-medium text-gray-900 dark:text-white"
                >
                  {{ $t("partage.cookies.analytics") }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {{ $t("partage.cookies.analyticsDescription") }}
                </p>
              </div>
            </label>

            <!-- Marketing -->
            <label
              class="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl cursor-pointer"
            >
              <input
                v-model="marketingConsent"
                type="checkbox"
                class="mt-0.5 size-4 rounded border-gray-300 dark:border-gray-600 text-[#212121] focus:ring-[#212121]"
              />
              <div class="flex-1">
                <p
                  class="text-sm font-medium text-gray-900 dark:text-white"
                >
                  {{ $t("partage.cookies.marketing") }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {{ $t("partage.cookies.marketingDescription") }}
                </p>
              </div>
            </label>
          </div>

          <div class="flex flex-wrap gap-2 justify-end pt-2">
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              @click="rejectAll"
            >
              {{ $t("partage.cookies.toutRefuser") }}
            </button>
            <button
              type="button"
              class="px-5 py-2 text-sm font-semibold rounded-xl bg-[#212121] dark:bg-white text-white dark:text-gray-900 hover:bg-[#333] dark:hover:bg-gray-100 transition-colors"
              @click="saveCustom"
            >
              {{ $t("partage.cookies.enregistrer") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
